import { useSitecoreContext } from 'hooks/useSitecoreContext';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import { useI18n } from 'next-localization';
import { useGetDocumentsQuery } from '@/store/documentListing/services/documentListingAPI';
import useHelpers from 'hooks/helpers';
import { pushDataLayerEvent } from 'hooks/useDataLayer';
import styles from './styles/DocumentListing.module.scss';
import eventBus from 'hooks/eventBus';
import React from 'react';

type DocumentListingBaseProps = {
  editionId: string;
};

const DocumentListingBase = ({ editionId }: DocumentListingBaseProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const { t } = useI18n();
  const { getErrorDictionary, localizeDate } = useHelpers();

  const { data, error, isLoading } = useGetDocumentsQuery(
    { editionId: editionId },
    {
      skip: isExperienceEditor,
    }
  );

  const getLinkText = (document: object) => {
    return localizeDate(document.figuresToDate, {
      month: 'short',
      year: 'numeric',
    });
  };

  const updateDataLayer = (item: any) => {
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      pushDataLayerEvent({
        event: 'file_download',
        file_url: item.documentUri.includes('https://')
          ? item.documentUri
          : `${origin}${item.documentUri}`,
        file_name: getLinkText(item),
      });
    }
  };

  const handleDocumentCLick = (event: React.MouseEvent<HTMLElement>): void => {
    eventBus.emit('click-event', event);
  };

  if (isExperienceEditor || isLoading) {
    return (
      <ul className={styles['document-list']}>
        {[...Array(5)].map((_, index) => (
          <li key={index}>
            <div className="py-3">
              <BaseSkeleton className="d-block" width="120px" height="24px" />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  if (error) {
    return <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />;
  }

  if (data.content && data.content.length !== 0) {
    return (
      <ul className="document-list">
        {data.content.map((document, index) => (
          <li key={index} className="py-3" onClick={handleDocumentCLick}>
            <a
              href={document.documentUri}
              target="_blank"
              className="d-inline-flex align-items-center gap-1"
              onClick={() => updateDataLayer(document)}
            >
              <span className="text-link">{getLinkText(document)}</span>

              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.2417 5.35382L11.9124 6.68309H16.3655L9.4533 13.5288C9.18744 13.7947 9.18744 14.1935 9.4533 14.4593C9.71915 14.7252 10.1179 14.7252 10.3838 14.4593L17.296 7.68004V12.1331L18.6252 10.8038V5.35382H13.2417Z"
                  fill="currentColor"
                />
                <path
                  d="M17.9604 13.9939C17.5616 13.9939 17.2957 14.2598 17.2957 14.6585V17.3171H6.32927V6.68294H9.98476C10.3835 6.68294 10.6494 6.41708 10.6494 6.0183C10.6494 5.61952 10.3835 5.35367 9.98476 5.35367H5.66463C5.26585 5.35367 5 5.61952 5 6.0183V17.9817C5 18.3805 5.26585 18.6464 5.66463 18.6464H17.9604C18.3591 18.6464 18.625 18.3805 18.625 17.9817V14.6585C18.625 14.3262 18.2927 13.9939 17.9604 13.9939Z"
                  fill="currentColor"
                />
              </svg>

              <span className="sr-only">{t('opensInNewWindow')}</span>
            </a>
          </li>
        ))}
      </ul>
    );
  } else {
    return <div className="body-default">{t('noResultsMessage')}</div>;
  }
};

export default DocumentListingBase;
